import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/auth.service';
import { permissionService, UserPermissions } from '../services/permission.service';
import { roleService } from '../services/role.service';
import { Role } from '../types/models';
import { setSecureJSON, removeSecureItem } from '../utils/localStorage';
import { encrypt, decrypt } from '../utils/encryption';

/**
 * Auth Store with Encrypted Local Storage
 * 
 * This store now encrypts all auth data before storing it in localStorage.
 * The encryption is handled by a custom storage implementation that:
 * 1. Encrypts data before storing (using AES encryption)
 * 2. Decrypts data when retrieving
 * 3. Automatically migrates existing unencrypted data to encrypted format
 * 4. Falls back to unencrypted storage if encryption fails
 */


// Types
interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  permissions: UserPermissions;
  phone?: string;
  location?: string;
  department?: string;
  profilePicture?: string;
}

export interface MockUser {
  id: string;
  _id?: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  password?: string;
  role?: string;
  roles?: string[];
  permissions?: string[];
  [key: string]: any;
}



// Auth Store State Interface
interface AuthState {
  // State
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  users: MockUser[];
  roles: Role[];
  userRoles: Record<string, string>;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;

  // Permission methods
  hasPermission: (permission: string) => boolean;
  hasModulePermission: (module: string, action: string) => boolean;
  hasSubModulePermission: (module: string, subModule: string, action: string) => boolean;

  // User management
  updateUserRole: (userId: string, role: string) => void;
  updateUserPermissions: (permissions: UserPermissions) => void;
  refreshUserPermissions: () => Promise<void>;
  refreshRoles: () => Promise<void>;
  refreshUsers: () => Promise<void>;
  forceRefreshRoles: () => Promise<void>;

  // Role management
  updateRole: (roleName: string, permissions: UserPermissions) => void;
  createRole: (role: Partial<Role>) => void;
  deleteRole: (roleName: string) => void;
  assignRoleToUser: (userId: string, roleName: string) => void;
  unassignRoleFromUser: (userId: string) => void;

  // Utility methods
  setLoading: (loading: boolean) => void;
  setUser: (user: User | null) => void;
  setAuthenticated: (authenticated: boolean) => void;
  initializeAuth: () => void;
}

// Helper functions
const transformPermissionsForBackend = (permissions: UserPermissions): Record<string, any> => {
  const simplified: Record<string, any> = {};

  if (permissions.employee_management) {
    simplified.employee_management = {};
    Object.entries(permissions.employee_management).forEach(([submodule, actions]) => {
      if (actions && typeof actions === 'object') {
        simplified.employee_management[submodule] = actions;
      }
    });
  }

  if (permissions.hiring) {
    simplified.hiring = {};
    Object.entries(permissions.hiring).forEach(([submodule, actions]) => {
      if (actions && typeof actions === 'object') {
        simplified.hiring[submodule] = actions;
      }
    });
  }

  if (permissions.project_management) {
    simplified.project_management = {};
    Object.entries(permissions.project_management).forEach(([submodule, actions]) => {
      if (actions && typeof actions === 'object') {
        simplified.project_management[submodule] = actions;
      }
    });
  }

  if (permissions.settings) {
    simplified.settings = {};
    Object.entries(permissions.settings).forEach(([submodule, actions]) => {
      if (actions && typeof actions === 'object') {
        simplified.settings[submodule] = actions;
      }
    });
  }

  if (permissions.system) {
    simplified.system = {};
    Object.entries(permissions.system).forEach(([submodule, actions]) => {
      if (actions && typeof actions === 'object') {
        simplified.system[submodule] = actions;
      }
    });
  }

  return simplified;
};

// Custom storage implementation for Zustand persist
const createEncryptedStorage = () => ({
  getItem: (name: string) => {
    try {
      const item = localStorage.getItem(name);
      if (!item) return null;

      // Decrypt the stored data
      const decrypted = decrypt(item);
      return decrypted;
    } catch (error) {
      console.error('Error getting encrypted auth storage:', error);
      return null;
    }
  },

  setItem: (name: string, value: any) => {
    try {
      // Encrypt the data before storing
      const encrypted = encrypt(JSON.stringify(value));
      localStorage.setItem(name, encrypted);
    } catch (error) {
      console.error('Error setting encrypted auth storage:', error);
      // Fallback to unencrypted storage if encryption fails
      localStorage.setItem(name, JSON.stringify(value));
    }
  },

  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch (error) {
      console.error('Error removing encrypted auth storage:', error);
    }
  }
});

// Create Zustand Store
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      user: null,
      isLoading: false, // Changed from true to false
      users: [],
      roles: [],
      userRoles: {},

      // Setter methods
      setLoading: (loading) => set({ isLoading: loading }),
      setUser: (user) => set({ user }),
      setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),

      // Initialize auth state from localStorage
      initializeAuth: () => {
        try {
          const storedUser = localStorage.getItem('auth-storage');
          if (storedUser) {
            let parsed;

            // Try to decrypt first (new encrypted format)
            try {
              const decrypted = decrypt(storedUser);
              if (decrypted) {
                parsed = JSON.parse(decrypted);
              }
            } catch (decryptError) {
              // If decryption fails, try to parse as plain JSON (old unencrypted format)
              try {
                parsed = JSON.parse(storedUser);
                // If this succeeds, the data was unencrypted, so we should re-encrypt it

                // The persist middleware will automatically re-encrypt this data
              } catch (parseError) {
                console.error('Failed to parse auth storage data:', parseError);
                return;
              }
            }

            if (parsed?.state?.user && parsed?.state?.isAuthenticated) {
              set({
                user: parsed.state.user,
                isAuthenticated: true,
                isLoading: false
              });
            }
          }
        } catch (error) {
          console.error('Error initializing auth state:', error);
        }
      },

      // Login method
      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true });
          const response = await authService.login({ email, password });

          let userData;
          if (response.user) {
            userData = response.user;
          } else {
            throw new Error('Invalid response structure from server');
          }

          // Handle Super Admin
          const isSuperAdmin =
            (Array.isArray(userData.roles) && userData.roles.some((r: string) => ['SUPER_ADMIN', 'Super Admin', 'super_admin'].includes(r))) ||
            (typeof userData.roles === 'string' && ['SUPER_ADMIN', 'Super Admin', 'super_admin'].includes(userData.roles));

          if (isSuperAdmin) {
            const allPermissionsObject = {
              employee_management: {
                employees: { view: true, create: true, edit: true, delete: true },
                department: { view: true, create: true, edit: true, delete: true },
                agreement: { view: true, create: true, edit: true, delete: true },
                compensation: { view: true, create: true, edit: true, delete: true }
              },
              sales_management: {
                client: { view: true, create: true, edit: true, delete: true },
                proposal: { view: true, create: true, edit: true, delete: true },
                contract: { view: true, create: true, edit: true, delete: true },
                revenue: { view: true, create: true, edit: true, delete: true }
              },
              finance_management: {
                invoice: { view: true, create: true, edit: true, delete: true },
                expense: { view: true, create: true, edit: true, delete: true },
                financialReport: { view: true }
              },
              hiring: {
                candidate: { view: true, create: true, edit: true, delete: true },
                jobPosting: { view: true, create: true, edit: true, delete: true },
                onboarding: { view: true, create: true, edit: true, delete: true },
                settings: {
                  onboardingTask: { view: true, create: true, edit: true, delete: true }
                }
              },
              project_management: {
                project: { view: true, create: true, edit: true, delete: true },
                task: { view: true, create: true, edit: true, delete: true },
                projectStatus: { view: true, create: true, edit: true, delete: true }
              },
              settings: {
                roleAndPermission: { view: true, create: true, edit: true, delete: true },
                jobTitle: { view: true, create: true, edit: true, delete: true }
              },
              system: {
                system: { view: true, create: true, edit: true, delete: true }
              }
            };

            const superAdminUser = {
              id: userData._id || 'super_admin',
              email: userData.email || 'superadmin@system.local',
              firstName: userData.firstName || 'Super',
              lastName: userData.lastName || 'Admin',
              role: 'Super Admin',
              permissions: allPermissionsObject,
            };

            set({
              user: superAdminUser,
              isAuthenticated: true,
              isLoading: false
            });
            setSecureJSON('currentUser', superAdminUser);
            return;
          }

          // Fetch permissions for regular users
          let permissionsData;
          try {
            permissionsData = await permissionService.getUserPermissions();
          } catch (permissionsError) {
            console.error('Failed to fetch permissions, using default permissions:', permissionsError);
            permissionsData = {
              permissions: {
                employee_management: {
                  employees: { view: false, create: false, edit: false, delete: false },
                  department: { view: false, create: false, edit: false, delete: false },
                  agreement: { view: false, create: false, edit: false, delete: false },
                  compensation: { view: false, create: false, edit: false, delete: false }
                },
                sales_management: {
                  client: { view: false, create: false, edit: false, delete: false },
                  proposal: { view: false, create: false, edit: false, delete: false },
                  contract: { view: false, create: false, edit: false, delete: false },
                  revenue: { view: false, create: false, edit: false, delete: false }
                },
                finance_management: {
                  invoice: { view: false, create: false, edit: false, delete: false },
                  expense: { view: false, create: false, edit: false, delete: false },
                  financialReport: { view: false }
                },
                hiring: {
                  candidate: { view: false, create: false, edit: false, delete: false },
                  jobPosting: { view: false, create: false, edit: false, delete: false },
                  onboarding: { view: false, create: false, edit: false, delete: false },
                  settings: {
                    onboardingTask: { view: false, create: false, edit: false, delete: false }
                  }
                },
                project_management: {
                  project: { view: false, create: false, edit: false, delete: false },
                  task: { view: false, create: false, edit: false, delete: false },
                  projectStatus: { view: false, create: false, edit: false, delete: false }
                },
                settings: {
                  roleAndPermission: { view: false, create: false, edit: false, delete: false },
                  jobTitle: { view: false, create: false, edit: false, delete: false }
                },
                system: {
                  system: { view: false, create: false, edit: false, delete: false }
                }
              } as UserPermissions,
              roles: userData.roles || []
            };
          }

          const userObject = {
            id: userData._id,
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            role: (() => {
              if (permissionsData.roles && permissionsData.roles.length > 0) {
                return permissionsData.roles[0];
              }
              if (userData.roles) {
                if (Array.isArray(userData.roles) && userData.roles.length > 0) {
                  return userData.roles[0];
                } else if (typeof userData.roles === 'string') {
                  return userData.roles;
                }
              }
              return 'Employee';
            })(),
            permissions: permissionsData.permissions,
          };

          set({
            user: userObject,
            isAuthenticated: true,
            isLoading: false
          });
          setSecureJSON('currentUser', userObject);

        } catch (error: any) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Register method
      register: async (email: string, password: string) => {
        const { users } = get();

        return new Promise<void>((resolve, reject) => {
          setTimeout(() => {
            if (users.some(u => u.email === email)) {
              reject(new Error("User already exists"));
              return;
            }

            const newUser: MockUser = {
              id: `u${users.length + 1}`,
              name: `User ${users.length + 1}`,
              email,
              password,
              role: 'EMPLOYEE',
              permissions: [],
            };

            set(prev => ({ users: [...prev.users, newUser] }));

            const permissionsObject = permissionService.convertArrayToPermissions(newUser.permissions || []);

            const registeredUser: User = {
              id: newUser.id,
              email: newUser.email,
              firstName: newUser.name?.split(' ')[0] || 'User',
              lastName: newUser.name?.split(' ')[1] || '',
              role: newUser.role || 'EMPLOYEE',
              permissions: permissionsObject,
            };

            set({
              user: registeredUser,
              isAuthenticated: true
            });
            setSecureJSON('currentUser', registeredUser);
            resolve();
          }, 500);
        });
      },

      // Logout method
      logout: () => {
        authService.logout();
        set({
          user: null,
          isAuthenticated: false
        });
        removeSecureItem('currentUser');
      },

      // Permission methods
      hasPermission: (permission: string): boolean => {
        const { isAuthenticated, user } = get();

        if (!isAuthenticated || !user) {
          return false;
        }

        const normalizedRole = (user.role || '').toLowerCase().replace(/\s*\(custom\)\s*$/i, '');
        const isSuperAdmin = normalizedRole === 'super admin' || normalizedRole === 'super_admin';

        if (isSuperAdmin) {
          return true;
        }

        if (!user.permissions || typeof user.permissions !== 'object') {
          return false;
        }

        // Handle SYSTEM_ADMIN permission
        if (permission === 'SYSTEM_ADMIN') {
          // Check if user has any system-level permissions
          const settingsPermissions = (user.permissions as any)?.settings;
          if (settingsPermissions) {
            // Check for jobTitle permissions or other system settings
            return Object.values(settingsPermissions).some((submodule: any) => {
              if (typeof submodule === 'object' && submodule !== null) {
                return Object.values(submodule).some((action: any) => action === true);
              }
              return false;
            });
          }
          return false;
        }

        // Handle dot notation permissions (e.g., 'settings.role.view', 'jobTitle.view')
        if (permission.includes('.')) {
          const parts = permission.split('.');
          let module, subModule, action;

          if (parts.length === 2) {
            // Format: 'jobTitle.view', 'agreement.view', 'compensation.view' -> module: 'settings' or 'employee_management', subModule: 'jobTitle'/'agreement'/'compensation', action: 'view'
            if (parts[0] === 'agreement' || parts[0] === 'compensation' || parts[0] === 'employees') {
              module = 'employee_management';
              subModule = parts[0] === 'employees' ? 'employees' : parts[0];
              action = parts[1];
            } else {
              module = 'settings';
              subModule = parts[0];
              action = parts[1];
            }
          } else if (parts.length === 3) {
            // Format: 'settings.role.view' -> module: 'settings', subModule: 'role', action: 'view'
            module = parts[0];
            subModule = parts[1];
            action = parts[2];
          } else {
            return false;
          }

          // Map virtual submodules to actual permissions structure
          if (module === 'settings' && (subModule === 'role' || subModule === 'user' || subModule === 'permission')) {
            const permissionValue = (user.permissions as any)?.[module]?.['roleAndPermission']?.[action];
            if (permissionValue === true) return true;

            // Fallback to original submodule name if roleAndPermission doesn't have it
            const fallbackValue = (user.permissions as any)?.[module]?.[subModule]?.[action];
            return fallbackValue === true;
          }

          const permissionValue = (user.permissions as any)?.[module]?.[subModule]?.[action];
          return permissionValue === true;
        }

        // Handle underscore notation permissions (e.g., 'EMPLOYEES_VIEW')
        const parts = permission.toLowerCase().split('_');
        if (parts.length < 2) {
          return false;
        }

        let module: string = '';
        let subModule: string = '';
        let action: string = '';

        if (parts[0] === 'employees' || parts[0] === 'department' || parts[0] === 'agreement' || parts[0] === 'compensation') {
          module = 'employee_management';
          if (parts[0] === 'employees') {
            subModule = 'employees';
          } else if (parts[0] === 'department') {
            subModule = 'department';
          } else if (parts[0] === 'agreement') {
            subModule = 'agreement';
          } else if (parts[0] === 'compensation') {
            subModule = 'compensation';
          }
          action = parts[1];
        } else if (parts[0] === 'candidate' || parts[0] === 'job' || parts[0] === 'status' || parts[0] === 'onboardingtask' || parts[0] === 'onboarding') {
          module = 'hiring';
          if (parts[0] === 'candidate') {
            subModule = 'candidate';
          } else if (parts[0] === 'job') {
            subModule = 'jobPosting';
          } else if (parts[0] === 'status') {
            subModule = 'status';
          } else if (parts[0] === 'onboardingtask') {
            subModule = 'onboardingTask';
          } else if (parts[0] === 'onboarding') {
            subModule = 'onboarding';
          }
          action = parts[1];
        } else if (parts[0] === 'user' || parts[0] === 'role' || parts[0] === 'permission') {
          module = 'settings';
          action = parts[1];

          const permissionValue = (user.permissions as any)?.[module]?.['roleAndPermission']?.[action];
          if (permissionValue === true) return true;

          // Fallback to original submodule name
          const fallbackValue = (user.permissions as any)?.[module]?.[parts[0]]?.[action];
          return fallbackValue === true;
        } else {
          const [fallbackModule, fallbackAction] = parts;
          if (!fallbackModule || !fallbackAction) return false;
          const modulePermissions = (user.permissions as any)?.[fallbackModule];
          if (!modulePermissions) return false;
          return Object.values(modulePermissions).some((sub: any) => sub[fallbackAction] === true);
        }

        if (!module || !subModule || !action) {
          return false;
        }

        const permissionValue = (user.permissions as any)?.[module]?.[subModule]?.[action];

        return permissionValue === true;
      },

      hasModulePermission: (module: string, action: string): boolean => {
        const { isAuthenticated, user } = get();

        if (!isAuthenticated || !user) {
          return false;
        }

        const normalizedRole = (user.role || '').toLowerCase().replace(/\s*\(custom\)\s*$/i, '');
        const isSuperAdmin = normalizedRole === 'super admin' || normalizedRole === 'super_admin';

        if (isSuperAdmin) {
          return true;
        }

        let moduleKey = module.toLowerCase().replace(/\s/g, '_');

        // CONSISTENT MAPPING
        if (moduleKey === 'sales' || moduleKey === 'sales_clients') {
          moduleKey = 'sales_management';
        }
        if (moduleKey === 'finance') {
          moduleKey = 'finance_management';
        }

        const modulePermissions = (user.permissions as any)?.[moduleKey];

        if (!modulePermissions) {
          return false;
        }

        return Object.values(modulePermissions).some((submodule: any) => {
          if (typeof submodule === 'object' && submodule !== null) {
            return submodule[action] === true;
          }
          return false;
        });
      },

      hasSubModulePermission: (module: string, subModule: string, action: string): boolean => {
        const { isAuthenticated, user } = get();

        if (!isAuthenticated || !user) {
          return false;
        }

        const normalizedRole = (user.role || '').toLowerCase().replace(/\s*\(custom\)\s*$/i, '');
        const isSuperAdmin = normalizedRole === 'super admin' || normalizedRole === 'super_admin';

        if (isSuperAdmin) {
          return true;
        }

        let moduleKey = module.toLowerCase().replace(/\s/g, '_');
        let subModuleKey = subModule.toLowerCase().replace(/\s/g, '_');

        // Map "sales" and "sales_clients" modules to "sales_management" in permissions structure
        if (moduleKey === 'sales' || moduleKey === 'sales_clients') {
          moduleKey = 'sales_management';
        }

        if (subModuleKey === 'employee') {
          subModuleKey = 'employees';
        }

        if (subModuleKey === 'jobposting') {
          subModuleKey = 'jobPosting';
        }

        if (subModuleKey === 'onboardingtask') {
          subModuleKey = 'onboardingTask';
        }

        if (subModuleKey === 'onboarding') {
          subModuleKey = 'onboarding';
        }

        if (subModuleKey === 'jobtitle') {
          subModuleKey = 'jobTitle';
        }

        if (subModuleKey === 'agreement' || subModuleKey === 'agreements') {
          subModuleKey = 'agreement';
        }

        if (subModuleKey === 'compensation') {
          subModuleKey = 'compensation';
        }

        if (subModuleKey === 'onboarding') {
          subModuleKey = 'onboarding';
        }

        if (subModuleKey === 'financialreport') {
          subModuleKey = 'financialReport';
        }
        if (subModuleKey === 'projectstatus') {
          subModuleKey = 'projectStatus';
        }

        const permissions = (user.permissions as any)?.[moduleKey]?.[subModuleKey];
        const hasPermission = typeof permissions === 'object' && permissions !== null && permissions[action] === true;

        return hasPermission;
      },

      // User management methods
      updateUserRole: (userId: string, newRole: string) => {
        set(prev => ({
          users: prev.users.map(u =>
            u.id === userId
              ? { ...u, role: newRole, permissions: [] }
              : u
          )
        }));

        const { user } = get();
        if (user?.id === userId) {
          set(prev => ({
            user: prev.user ? {
              ...prev.user,
              role: newRole,
              permissions: {
                employee_management: {
                  employees: { view: false, create: false, edit: false, delete: false },
                  department: { view: false, create: false, edit: false, delete: false },
                  agreement: { view: false, create: false, edit: false, delete: false },
                  compensation: { view: false, create: false, edit: false, delete: false }
                },
                sales_management: {
                  client: { view: false, create: false, edit: false, delete: false },
                  proposal: { view: false, create: false, edit: false, delete: false },
                  contract: { view: false, create: false, edit: false, delete: false },
                  revenue: { view: false, create: false, edit: false, delete: false }
                },
                finance_management: {
                  invoice: { view: false, create: false, edit: false, delete: false },
                  expense: { view: false, create: false, edit: false, delete: false },
                  financialReport: { view: false }
                },
                hiring: {
                  candidate: { view: false, create: false, edit: false, delete: false },
                  jobPosting: { view: false, create: false, edit: false, delete: false },
                  onboarding: { view: false, create: false, edit: false, delete: false },
                  settings: {
                    onboardingTask: { view: false, create: false, edit: false, delete: false }
                  }
                },
                project_management: {
                  project: { view: false, create: false, edit: false, delete: false },
                  task: { view: false, create: false, edit: false, delete: false },
                  projectStatus: { view: false, create: false, edit: false, delete: false }
                },
                settings: {
                  roleAndPermission: { view: false, create: false, edit: false, delete: false },
                  jobTitle: { view: false, create: false, edit: false, delete: false }
                },
                system: {
                  system: { view: false, create: false, edit: false, delete: false }
                }
              } as UserPermissions
            } : null
          }));
        }
      },

      updateUserPermissions: (permissions: UserPermissions) => {
        set(prev => ({
          user: prev.user ? {
            ...prev.user,
            permissions
          } : null
        }));
        setSecureJSON('currentUser', get().user);
      },

      refreshUserPermissions: async () => {
        try {
          const { permissions } = await permissionService.refreshPermissions();

          set(prev => ({
            user: prev.user ? { ...prev.user, permissions } : null
          }));

        } catch (error) {
          console.error('Failed to refresh user permissions:', error);
        }
      },

      refreshRoles: async () => {
        try {

          const res = await roleService.getRoles();
          const rolesArray = res.data?.data || res.data || [];



          const rolesWithIds = Array.isArray(rolesArray) ? rolesArray.map((role: Role) => ({
            ...role,
            id: role.id || role._id,
            _id: role._id || role.id,
            permissions: role.permissions || {}
          })) : [];

          set({ roles: rolesWithIds });

        } catch (err: any) {
          console.error('❌ Error refreshing roles:', err);
          // Don't throw error, just log it
        }
      },

      forceRefreshRoles: async () => {
        try {

          // Clear existing roles first
          set({ roles: [] });

          const res = await roleService.getRoles();
          const rolesArray = res.data?.data || res.data || [];



          const rolesWithIds = Array.isArray(rolesArray) ? rolesArray.map((role: Role) => ({
            ...role,
            id: role.id || role._id,
            _id: role._id || role.id,
            permissions: role.permissions || {}
          })) : [];

          set({ roles: rolesWithIds });

        } catch (err: any) {
          console.error('❌ Error force refreshing roles:', err);
        }
      },

      refreshUsers: async () => {
        try {
          const { getEmployees } = await import('../services/employee.service');
          const employeesData = await getEmployees();

          const usersArray = Array.isArray(employeesData) ? employeesData : [];

          const processedUsers = usersArray.map((emp: any) => {
            const user = emp.user || emp;
            const name = user.name ||
              `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
              user.email ||
              'Unknown User';

            let role = '';
            let roles: string[] = [];

            if (user.roles && Array.isArray(user.roles)) {
              roles = user.roles.map((r: any) => typeof r === 'object' ? r.name || r._id || r.id : r).filter(Boolean);
              role = roles[0] || '';
            }

            if (!role && emp.role) {
              role = typeof emp.role === 'object' ? emp.role.name || emp.role._id || emp.role.id : emp.role;
            }

            if (!role && user.role) {
              role = typeof user.role === 'object' ? user.role.name || user.role._id || user.role.id : user.role;
            }

            return {
              id: user._id || user.id || emp._id || emp.id,
              _id: user._id || emp._id,
              name: name,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email || emp.email,
              role: role,
              roles: roles,
              ...user,
              ...emp
            };
          });

          set({ users: processedUsers });
        } catch (error) {
          console.error('Failed to refresh users:', error);
        }
      },

      // Role management methods
      updateRole: async (roleName: string, permissions: UserPermissions) => {
        try {
          const systemRoles = ['SUPER_ADMIN', 'Super Admin', 'super_admin'];
          if (systemRoles.includes(roleName) || systemRoles.some(role => role.toLowerCase() === roleName.toLowerCase())) {
            throw new Error(`Cannot edit system role "${roleName}". System roles are protected.`);
          }

          const { roles } = get();
          let role = roles.find(r => r.name === roleName);

          if (!role) {
            throw new Error(`Role "${roleName}" could not be found in the system.`);
          }

          const roleId = role.id || role._id;

          if (!roleId) {
            throw new Error("Role ID not found.");
          }

          const simplifiedPermissions = transformPermissionsForBackend(permissions);

          await roleService.updateRole(roleId, { permissions: simplifiedPermissions });

          // Refresh roles after update
          get().refreshRoles();

        } catch (err: any) {
          console.error('Failed to update role:', err);
          throw err;
        }
      },

      createRole: async (role: Partial<Role>) => {
        try {
          // Send role data with permissions object
          const roleData = {
            name: role.name,
            description: role.description || '',
            permissions: role.permissions // Pass the permissions object
          };

          await roleService.createRole(roleData);

          // Refresh roles after creation
          get().refreshRoles();

        } catch (err: any) {
          console.error('Failed to create role:', err);
          throw err;
        }
      },

      deleteRole: async (roleName: string) => {
        try {
          const systemRoles = ['SUPER_ADMIN', 'Super Admin', 'super_admin'];
          if (systemRoles.includes(roleName) || systemRoles.some(role => role.toLowerCase() === roleName.toLowerCase())) {
            throw new Error(`Cannot delete system role "${roleName}". System roles are protected.`);
          }

          const { roles } = get();
          const role = roles.find(r => r.name === roleName);

          if (!role) {
            throw new Error("The specified role could not be found in the system.");
          }

          const roleId = role.id || role._id;
          if (!roleId) {
            throw new Error("Role ID not found.");
          }

          await roleService.deleteRole(roleId);

          // Refresh roles after deletion
          get().refreshRoles();

        } catch (err: any) {
          console.error('Failed to delete role:', err);
          throw err;
        }
      },

      assignRoleToUser: (userId: string, roleName: string) => {
        const systemRoles = ['SUPER_ADMIN', 'Super Admin', 'super_admin'];
        if (systemRoles.includes(roleName) || systemRoles.some(role => role.toLowerCase() === roleName.toLowerCase())) {
          throw new Error(`Cannot assign system role "${roleName}" to users. System roles are protected.`);
        }

        const { roles } = get();
        const role = roles.find(r => r.name === roleName);
        const permissionsObject = role ? role.permissions : {};

        set(prev => ({
          users: prev.users.map(user =>
            user.id === userId
              ? { ...user, role: roleName, permissions: permissionService.convertPermissionsToArray(permissionsObject as UserPermissions) }
              : user
          )
        }));

        const { user } = get();
        if (user?.id === userId) {
          get().refreshUserPermissions();
        }
      },

      unassignRoleFromUser: (userId: string) => {
        const { roles } = get();
        const defaultRole = roles.find(r => r.name === 'Employee');
        const defaultPermissionsObject = defaultRole ? defaultRole.permissions : {};

        set(prev => ({
          users: prev.users.map(user =>
            user.id === userId
              ? { ...user, role: 'Employee', permissions: permissionService.convertPermissionsToArray(defaultPermissionsObject as UserPermissions) }
              : user
          )
        }));

        const { user } = get();
        if (user?.id === userId) {
          get().refreshUserPermissions();
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      } as Partial<AuthState>),
      storage: createEncryptedStorage() as any
    }
  )
);

// Export a hook for easier usage
export const useAuth = () => {
  return useAuthStore();
}; 